/**
 * Sentry browser SDK initialisation with PII scrubbing.
 *
 * - DSN is read from `VITE_SENTRY_DSN`; absent → falls back to `console.error`
 *   with structured JSON (no Sentry call, no PII leak).
 * - Breadcrumb scrubber strips email / workspace_id / click_id / email_hash from
 *   URL query strings and request payloads.
 * - `beforeSend` redacts the same fields from the event itself.
 * - Sample rates kept conservative until we have real traffic data.
 *
 * Per `docs/security.md` §Dashboard, no PII may land in breadcrumbs.
 */

import * as Sentry from '@sentry/react';

// `q` is the founder admin People search (T-170); a searched email must not
// land in a breadcrumb URL.
const PII_QUERY_PARAMS = ['email', 'workspace_id', 'click_id', 'cl', 'email_hash', 'q'] as const;
const PII_PAYLOAD_FIELDS = ['email', 'workspace_id', 'click_id', 'email_hash', 'password'] as const;

function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const u = new URL(url, 'https://placeholder.invalid');
    let mutated = false;
    for (const p of PII_QUERY_PARAMS) {
      if (u.searchParams.has(p)) {
        u.searchParams.set(p, '[scrubbed]');
        mutated = true;
      }
    }
    return mutated ? u.toString().replace('https://placeholder.invalid', '') : url;
  } catch {
    return url;
  }
}

function scrubPayload(data: unknown): unknown {
  if (data === null || typeof data !== 'object') return data;
  if (Array.isArray(data)) return data.map(scrubPayload);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
    if ((PII_PAYLOAD_FIELDS as readonly string[]).includes(k)) {
      out[k] = '[scrubbed]';
    } else {
      out[k] = scrubPayload(v);
    }
  }
  return out;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    // Console fallback so unhandled errors still surface in dev.
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        console.error(
          JSON.stringify({
            level: 'error',
            kind: 'unhandled_error',
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
          }),
        );
      });
      window.addEventListener('unhandledrejection', (event) => {
        console.error(
          JSON.stringify({
            level: 'error',
            kind: 'unhandled_rejection',
            reason: String(event.reason),
          }),
        );
      });
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE_SHA as string | undefined,
    tracesSampleRate: 0.05,
    // Replay disabled at MVP. It's ~80KB gzipped and adds DOM-mutation observers
    // which we don't want firing on every dashboard interaction. Re-enable once
    // we have a real reason (regression-hunting). The breadcrumb scrubber + the
    // explicit beforeSend redaction still cover PII guarantees without it.
    integrations: [Sentry.browserTracingIntegration()],
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data && typeof breadcrumb.data === 'object') {
        const data = breadcrumb.data as Record<string, unknown>;
        if (typeof data.url === 'string') {
          data.url = scrubUrl(data.url);
        }
      }
      if (breadcrumb.message) {
        breadcrumb.message = breadcrumb.message
          .replace(
            /(?:email|workspace_id|click_id|email_hash)=([^&\s]+)/gi,
            (_m, _v) => `${_m.split('=')[0]}=[scrubbed]`,
          )
          .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[scrubbed-email]');
      }
      return breadcrumb;
    },
    beforeSend(event) {
      if (event.request) {
        event.request.url = scrubUrl(event.request.url);
        event.request.headers = undefined;
        event.request.cookies = undefined;
        if (event.request.data) {
          event.request.data = scrubPayload(event.request.data);
        }
      }
      if (event.user) {
        event.user.email = undefined;
        event.user.ip_address = undefined;
      }
      return event;
    },
  });
}

export { Sentry };
